import { TemplateMarkdownPreview } from '@/components/template-markdown-preview';
import {
  SegmentedControl,
  SegmentedControlContent,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { Skeleton } from '@heidi/design-system/skeleton';
import { focusRingWithin } from '@heidi/design-system/styles';
import { TypographyCaption } from '@heidi/design-system/typography';
import { communityTemplateDetailsMessages } from '@heidi/templates';
import type { DetailedPublicTemplateResponse } from '@heidi/templates/contracts';
import { Blocks, NotebookPen } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

interface TemplatePreviewSectionProps {
  isLoading: boolean;
  template: DetailedPublicTemplateResponse | undefined;
}

interface MarkdownScrollAreaProps {
  'data-testid': string;
  markdown: string;
}

// Outline suppressed because the panel draws the ring; otherwise the browser default paints
// a second one inside it.
const MarkdownScrollArea = ({
  'data-testid': testId,
  markdown,
}: MarkdownScrollAreaProps) => (
  <div
    tabIndex={0}
    className="max-h-[35rem] min-h-[150px] overflow-y-auto outline-none focus-visible:outline-none"
    data-testid={testId}
  >
    <TemplateMarkdownPreview markdown={markdown} />
  </div>
);

export const TemplatePreviewSection = ({
  isLoading,
  template,
}: TemplatePreviewSectionProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-52 w-full" />
      </div>
    );
  }

  return (
    <SegmentedControl defaultValue="example" className="w-full gap-4">
      <SegmentedControlList className="w-full">
        <SegmentedControlTrigger
          value="example"
          data-testid="community-template-preview-example-tab-trigger"
        >
          <NotebookPen />
          <FormattedMessage {...communityTemplateDetailsMessages.exampleNoteTab} />
        </SegmentedControlTrigger>
        <SegmentedControlTrigger
          value="structure"
          data-testid="community-template-preview-structure-tab-trigger"
        >
          <Blocks />
          <FormattedMessage {...communityTemplateDetailsMessages.templateStructureTab} />
        </SegmentedControlTrigger>
      </SegmentedControlList>
      <SegmentedControlContent
        value="example"
        data-testid="community-template-preview-example-panel"
        // focusRingWithin, not focusRing — the focusable element is the scroll area inside.
        // tabIndex -1 hands the panel's Radix-default stop to that scroll area.
        className={focusRingWithin}
        tabIndex={-1}
      >
        <MarkdownScrollArea
          data-testid="community-template-example-scroll-area"
          markdown={template?.template_content.example_output ?? ''}
        />
        <div
          className="border-t border-border pt-3"
          data-testid="community-template-example-disclaimer"
        >
          <TypographyCaption className="text-text-tertiary">
            <FormattedMessage
              {...communityTemplateDetailsMessages.exampleNoteDisclaimer}
            />
          </TypographyCaption>
        </div>
      </SegmentedControlContent>
      <SegmentedControlContent
        value="structure"
        data-testid="community-template-preview-structure-panel"
        // Rationale on the example panel above.
        className={focusRingWithin}
        tabIndex={-1}
      >
        <MarkdownScrollArea
          data-testid="community-template-structure-scroll-area"
          markdown={template?.template_content.structure ?? ''}
        />
      </SegmentedControlContent>
    </SegmentedControl>
  );
};
